import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { EpicSpan, EpicTask, EpicWithStatus, LooseTimelineItem } from '../../data/interfaces/epic.interface'
import { WorklogService } from '../../data/services/worklog.service'
import { AREA_META, BOARD_COLUMNS, STATUS_META, TYPE_META } from '../../data/constants/worklog.constants'
import { EPIC_BAR_COLOR, EPIC_STATUS_META } from '@modules/private/roadmap/data/constants/roadmap.constants'

/** Tramo sólido dentro de una barra, en % de la propia barra (no del carril). */
interface BarSegment {
  left: number
  width: number
  /** La tarea sigue en vuelo: el tramo se dibuja abierto por la derecha. */
  open: boolean
}

/** Barra de una tarea dentro del desglose de su épica. */
interface TaskBar {
  task: EpicTask
  left: number
  width: number
  /**
   * Qué % de la barra fue DESARROLLO. El resto —de la entrega a la liberación— va rayado: una
   * tarea lista el lunes y liberada dos semanas después no costó dos semanas (ADR-0048 · D2).
   */
  devWidth: number
  /** Sin fecha de inicio no hay duración que dibujar: se pinta como hito puntual. */
  point: boolean
  color: string
  tooltip: string
}

/** Fila de una tarea o bug que no cuelga de ninguna épica: trabajo de una sola pieza. */
interface LooseRow {
  item: LooseTimelineItem
  left: number
  width: number
  /** Sin duración medible (sin arranque, sin entrega, o registrada en retroactivo): hito puntual. */
  point: boolean
  color: string
  typeColor: string
  typeIcon: string
  /** Etapa actual, solo mientras esté en curso — es lo que la vista de Tiempos daba antes. */
  stageLabel: string | null
  stageColor: string
  stageStep: number
  /** Tramo entre la fecha comprometida y hoy, cuando ya se pasó. Se pinta en rojo sobre la barra. */
  overdueLeft: number | null
  overdueWidth: number
  /** % de la barra que fue desarrollo; el resto es espera de liberación. */
  devWidth: number
  durationLabel: string
  tooltip: string
}

/** Las tres bandas de la línea de tiempo. */
interface Band {
  key: 'live' | 'done' | 'planned'
  label: string
  icon: string
  epics: GanttRow[]
  loose: LooseRow[]
}

/** Fila del Gantt: una épica, su barra y su desglose (perezoso). */
interface GanttRow {
  epic: EpicWithStatus
  /** La ENVOLVENTE: de la primera tarea a la última. Es la pista pálida, no la duración. */
  left: number
  width: number
  /** El trabajo real: un tramo sólido por tarea, con los huecos a la vista. */
  segments: BarSegment[]
  /** % de la barra correspondiente a tareas ya cerradas */
  progress: number
  statusColor: string
  statusLabel: string
  rangeLabel: string
  /** Trabajo real. Es el número grande, y lo que se preguntaba cuando se leía "42 d". */
  durationLabel: string
  /** Cuánto estuvo abierta. Solo se muestra cuando difiere del trabajo, o sería ruido. */
  elapsedLabel: string | null
  //! Se calcula y no lo lee nadie: ni la plantilla ni el TS. Queda reportado como código
  //! muerto — se arrastra el rename, borrarlo es otra decisión.
  areas: { name: string; color: string }[]
  expanded: boolean
  loading: boolean
  tasks: TaskBar[] | null
}

interface MonthHead {
  label: string
  left: number
  width: number
  isCurrent: boolean
}

const MS_DAY = 86400000
const MS_HOUR = 3600000
const MONTHS_ES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC']

/**
 * Etiqueta de duración. Por debajo de 48 h va en horas, y ahí está la razón: con un solo
 * desarrollador que trabaja a ráfagas, redondear a días convierte una tarde en "1 d" y un viernes
 * por la noche retomado el lunes en "3 d". La mayoría del trabajo real cae en este tramo.
 */
function durLabel(ms: number | null): string | null {
  if (ms == null || ms < 0) return null
  if (ms < MS_HOUR) return `${Math.max(Math.round(ms / 60000), 1)} min`
  if (ms < 2 * MS_DAY) {
    const h = Math.floor(ms / MS_HOUR)
    const m = Math.round((ms % MS_HOUR) / 60000)
    return m ? `${h} h ${m} m` : `${h} h`
  }
  return `${Math.round(ms / MS_DAY)} d`
}

/**
 * Gantt de ÉPICAS (ADR-0040 · D13). Cada épica es una barra morada que abarca del arranque de su
 * primera tarea a la entrega de la última, y se despliega para ver las tareas que la compusieron.
 *
 * El eje son FECHAS REALES, no sprints: el span sale del trabajo (`flowStartedAt`/`deliveredAt`),
 * así que una épica que cruzó tres sprints se ve del largo que de verdad tuvo. La versión anterior
 * dibujaba columnas de sprint sobre `roadmap.json`, donde el rango se declaraba a mano.
 *
 * Las épicas sin trabajo cargado no se grafican —no hay duración que mostrar— pero se cuentan al
 * pie: omitirlas en silencio haría leer el Gantt como si fuera todo el roadmap.
 */
@Component({
  selector: 'app-worklog-gantt',
  templateUrl: './worklog-gantt.component.html',
  styleUrls: ['./worklog-gantt.component.scss']
})
export class WorklogGanttComponent implements OnChanges {
  @Input() epics: EpicWithStatus[] | null = null
  /** Tareas y bugs sin épica. `null` = esta instancia solo dibuja épicas. */
  @Input() loose: LooseTimelineItem[] | null = null
  /** Fecha de cierre del sprint activo: es el compromiso por defecto cuando la tarea no trae uno. */
  @Input() sprintEnd: string | null = null
  @Output() select = new EventEmitter<EpicWithStatus>()
  @Output() openItem = new EventEmitter<string>()

  rows: GanttRow[] = []
  bands: Band[] = []
  months: MonthHead[] = []
  todayLeft: number | null = null
  /** Ancho mínimo del carril para que un roadmap largo no se aplaste: se hace scroll. */
  trackWidth = 0
  /**
   * Épicas sin trabajo: no se dibujan —no tienen duración que pintar— pero **sí se pueden abrir**.
   *
   * Antes solo se contaban. El problema de contarlas nada más es que una épica registrada de más no
   * aparece en ninguna otra pantalla: el Backlog solo agrupa las que tienen trabajo en cola, y la
   * ficha de Métricas es UI muerta (su selector nunca se emite). Sin esta lista, una épica vacía no
   * se puede ni mirar, mucho menos retirar — que es exactamente lo que pedía F-037.
   */
  sinTrabajo: EpicWithStatus[] = []
  /** Sueltas que llegaron pero no entraron en ninguna banda (en cola, sin compromiso). */
  undrawnLoose = 0

  readonly EPIC_BAR_COLOR = EPIC_BAR_COLOR
  /** Cuántas etapas tiene el pipeline: el denominador del "paso 2/6" de una tarea en curso. */
  readonly TOTAL_STAGES = BOARD_COLUMNS.length

  private t0 = 0
  private span = 1

  constructor(private worklog: WorklogService) {}

  ngOnChanges(): void {
    this.build()
  }

  private build(): void {
    //* Se preserva la expansión al reconstruir: `epics` y `loose` llegan en respuestas separadas, y
    //* sin esto la segunda borraría el desglose que el usuario acabara de abrir con la primera.
    const abiertas = new Map(this.rows.filter((r) => r.expanded).map((r) => [r.epic.epic.slug, r]))

    this.rows = []
    this.bands = []
    this.undrawnLoose = 0
    this.sinTrabajo = []
    this.months = []
    this.todayLeft = null

    const epicasConSpan = (this.epics ?? []).filter((e) => !!e.startedAt)
    this.sinTrabajo = (this.epics ?? []).filter((e) => !e.startedAt)
    //* Lo suelto que ni arrancó ni tiene compromiso no se dibuja: el backend ya lo omite, pero el
    //* filtro se repite aquí porque esta instancia también se usa sin carril suelto.
    const sueltas = (this.loose ?? []).filter((i) => i.startedAt || i.deliveredAt || i.dueDate)
    if (!epicasConSpan.length && !sueltas.length) return

    //* El eje se redondea a meses completos para que las bandas de mes embonen con el carril, y
    //* abarca los DOS carriles: si una tarea suelta arrancó antes que cualquier épica, el eje crece.
    const hitos: number[] = []
    for (const e of epicasConSpan) {
      hitos.push(new Date(e.startedAt as string).getTime())
      //* El cierre es la liberación (entrada a Terminadas), no la entrega: sin esto el eje se corta antes del
      //* tramo de espera de liberación y las barras se salen del carril.
      hitos.push(new Date(e.endedAt ?? e.deliveredAt ?? (e.startedAt as string)).getTime())
    }
    for (const i of sueltas) {
      for (const f of [i.startedAt, i.deliveredAt, i.releasedAt, i.dueDate]) if (f) hitos.push(new Date(f).getTime())
    }

    const now = Date.now()
    const first = new Date(Math.min(...hitos))
    const last = new Date(Math.max(...hitos, now))
    this.t0 = new Date(first.getFullYear(), first.getMonth(), 1).getTime()
    const t1 = new Date(last.getFullYear(), last.getMonth() + 1, 1).getTime()
    this.span = Math.max(t1 - this.t0, MS_DAY)

    this.months = this.monthBands(t1, now)
    this.trackWidth = Math.max(720, this.months.length * 96)
    this.todayLeft = now >= this.t0 && now <= t1 ? this.pct(now) : null

    this.rows = epicasConSpan
      .sort((a, b) => new Date(a.startedAt as string).getTime() - new Date(b.startedAt as string).getTime())
      .map((e) => {
        const row = this.toRow(e, now)
        const previa = abiertas.get(e.epic.slug)
        if (previa) {
          row.expanded = true
          row.tasks = previa.tasks
          row.loading = previa.loading
        }
        return row
      })

    //* Sin carril suelto no hay nada que separar: la vista de solo épicas se queda como estaba.
    if (this.loose === null) return
    this.bands = this.toBands(sueltas, now)
    this.undrawnLoose = this.countUndrawnLoose(sueltas)
  }

  /**
   * Reparte todo en tres bandas. La separación es la pregunta que responde cada una: qué está
   * corriendo, qué ya se entregó, y qué está comprometido pero aún no arranca.
   */
  private toBands(sueltas: LooseTimelineItem[], now: number): Band[] {
    const enCurso: LooseRow[] = []
    const entregado: LooseRow[] = []
    const programado: LooseRow[] = []

    for (const i of sueltas) {
      const row = this.toLooseRow(i, now)
      //* La banda la decide el ESTADO, no solo las fechas: una tarea que salió al flujo y volvió al
      //* Backlog conserva su `flowStartedAt`, y por fechas parecería en curso cuando está en cola.
      if (i.deliveredAt) entregado.push(row)
      else if (row.stageLabel) enCurso.push(row)
      //* Sin arrancar entra SOLO lo que tiene fecha comprometida: eso se ve venir. Una tarea que
      //* salió al flujo y volvió al Backlog conserva su `flowStartedAt`, pero sin compromiso no hay
      //* nada que anticipar — sería una fila de cola más, que es el ruido que esta vista evita.
      else if (i.dueDate) programado.push(row)
    }

    const epicasEnCurso = this.rows.filter((r) => r.epic.status !== 'achieved')
    const epicasHechas = this.rows.filter((r) => r.epic.status === 'achieved')

    return [
      { key: 'live', label: 'En curso', icon: 'fa-solid fa-bolt', epics: epicasEnCurso, loose: enCurso },
      { key: 'done', label: 'Entregado', icon: 'fa-solid fa-circle-check', epics: epicasHechas, loose: entregado },
      { key: 'planned', label: 'Programado', icon: 'fa-regular fa-calendar-check', epics: [], loose: programado },
    ].filter((b) => b.epics.length || b.loose.length) as Band[]
  }

  /** Cuántas sueltas llegaron y no se dibujaron: se declaran al pie, no se omiten en silencio. */
  private countUndrawnLoose(sueltas: LooseTimelineItem[]): number {
    return sueltas.length - this.bands.reduce((n, b) => n + b.loose.length, 0)
  }

  /**
   * Una tarea o bug suelto. Reutiliza la geometría del desglose de las épicas: barra si hay
   * recorrido medible, rombo si no.
   */
  private toLooseRow(i: LooseTimelineItem, now: number): LooseRow {
    const area = (i.areas ?? [])[0]
    const color = (AREA_META as any)[area]?.color ?? '#94a3b8'
    const tipo = (TYPE_META as any)[i.type] ?? TYPE_META.task
    const start = i.startedAt ? new Date(i.startedAt).getTime() : null
    const entregada = i.deliveredAt ? new Date(i.deliveredAt).getTime() : null
    //* El reloj para al liberarse (ADR-0048 · D2). Entregada pero sin liberar, la barra
    //* sigue creciendo: lo que falta por pasar es espera de ventana, y se ve rayado.
    const liberada = i.releasedAt ? new Date(i.releasedAt).getTime() : null
    const fin = liberada ?? entregada

    //* Una tarea en curso llega hasta HOY: su barra sigue creciendo. Sin `observed` no se afirma
    //* duración alguna — las fechas existen pero el recorrido entre ellas no se midió.
    const end = fin ?? (start ? now : null)
    const point = !i.observed || start == null || end == null
    const ancla = start ?? fin ?? (i.dueDate ? new Date(i.dueDate).getTime() : null)

    const meta = (STATUS_META as any)[i.status]
    //* En el flujo = en alguna columna del tablero. `Backlog` no cuenta aunque tenga fechas, y los
    //* estados terminales tampoco: de esos ya habla `deliveredAt`.
    const enCurso = !!meta && meta.order > 0 && !fin && i.status !== 'Cerrada' && i.status !== 'Cancelada'
    //* Qué parte de la barra fue desarrollo. Sin entrega todavía es desarrollo entero.
    const devWidth = start != null && end != null && entregada != null
      ? Math.min(Math.max(((entregada - start) / Math.max(end - start, 1)) * 100, 0), 100)
      : 100

    //* El atraso, dibujado: el tramo entre la fecha comprometida y hoy. La fecha sale de la tarea y,
    //* si no trae, del cierre del sprint — misma regla que usaba la vista de Tiempos.
    const compromiso = i.dueDate ?? (enCurso ? this.sprintEnd : null)
    const venc = compromiso ? new Date(compromiso).getTime() : null
    const atrasado = enCurso && venc != null && venc < now
    //* Si la fecha comprometida quedó ANTES del arranque —se pactó para algo que empezó tarde— el
    //* tramo se recorta al inicio de la barra: dibujarlo a la izquierda sugeriría trabajo que no hubo.
    const desdeAtraso = atrasado ? Math.max(venc as number, start as number) : null

    return {
      item: i,
      left: point ? (ancla == null ? 0 : this.pct(ancla)) : this.pct(start as number),
      width: point ? 0 : Math.max(this.pct(end as number) - this.pct(start as number), 0.4),
      point,
      color,
      typeColor: tipo.color,
      typeIcon: tipo.icon,
      stageLabel: enCurso && meta ? meta.label : null,
      stageColor: meta?.color ?? '#94a3b8',
      stageStep: meta?.order ?? 0,
      overdueLeft: desdeAtraso != null ? this.pct(desdeAtraso) : null,
      overdueWidth: desdeAtraso != null ? Math.max(this.pct(now) - this.pct(desdeAtraso), 0.4) : 0,
      devWidth,
      durationLabel: point
        ? compromiso
          ? `para el ${new Date(compromiso).getDate()} ${MONTHS_ES[new Date(compromiso).getMonth()].toLowerCase()}`
          : 'sin duración medida'
        : durLabel((end as number) - (start as number)) ?? 'sin duración medida',
      tooltip: `${i.code ?? ''} · ${i.title}${i.points != null ? ` · ${i.points} pts` : ''}`,
    }
  }

  /** Una banda por mes del eje. Como `t0`/`t1` son inicios de mes, las bandas tapizan el carril. */
  private monthBands(t1: number, now: number): MonthHead[] {
    const out: MonthHead[] = []
    const curKey = new Date(now).getFullYear() * 12 + new Date(now).getMonth()
    for (let d = new Date(this.t0); d.getTime() < t1; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime()
      out.push({
        label: `${MONTHS_ES[d.getMonth()]} ${d.getFullYear()}`,
        left: this.pct(d.getTime()),
        width: this.pct(end) - this.pct(d.getTime()),
        isCurrent: d.getFullYear() * 12 + d.getMonth() === curKey
      })
    }
    return out
  }

  private toRow(e: EpicWithStatus, now: number): GanttRow {
    const start = new Date(e.startedAt as string).getTime()
    //* Sin `endedAt` la épica NO ha terminado —le falta al menos una tarea por liberarse—,
    //* así que la envolvente llega hasta hoy en vez de fingir un cierre (ADR-0048 · D3).
    const end = e.endedAt ? new Date(e.endedAt).getTime() : now
    const meta = EPIC_STATUS_META[e.status]

    const trabajo = durLabel(e.workedMs)
    //* La envolvente solo se dice cuando difiere de verdad del trabajo. Repetir "4 h · abierta 4 h"
    //* es ruido; "4 h · abierta 42 d" es justo lo que no se veía.
    const abierta = e.elapsedMs != null && e.workedMs != null && e.elapsedMs > e.workedMs * 1.5 ? durLabel(e.elapsedMs) : null

    return {
      epic: e,
      left: this.pct(start),
      //* Una épica de un solo día colapsaría a 0%: se le da un mínimo visible.
      width: Math.max(this.pct(end) - this.pct(start), 0.6),
      segments: this.toSegments(e.spans ?? [], start, end, now),
      progress: e.taskCount ? Math.round((e.doneCount / e.taskCount) * 100) : 0,
      statusColor: meta.color,
      statusLabel: meta.label,
      rangeLabel: this.rangeLabel(start, end),
      durationLabel: trabajo ?? 'sin medir',
      elapsedLabel: abierta,
      areas: (e.epic.areas ?? []).map((p) => ({ name: p.name, color: p.color })),
      expanded: false,
      loading: false,
      tasks: null
    }
  }

  /**
   * Los tramos de trabajo, en % de la PROPIA barra y no del carril: así van dentro de ella y no
   * hace falta una segunda capa absoluta que mantener alineada con la primera.
   *
   * No se fusionan los solapados a propósito — pintados uno encima de otro se ven igual, y la
   * cifra de la etiqueta ya viene del backend, que sí hace la unión.
   */
  private toSegments(spans: EpicSpan[], start: number, end: number, now: number): BarSegment[] {
    const total = Math.max(end - start, 1)
    const rel = (t: number) => ((t - start) / total) * 100

    return spans
      .map((sp) => ({ a: new Date(sp.from).getTime(), b: sp.to ? new Date(sp.to).getTime() : now, open: !sp.to }))
      .filter((sp) => Number.isFinite(sp.a) && Number.isFinite(sp.b) && sp.b >= sp.a)
      .sort((x, y) => x.a - y.a)
      .map((sp) => ({
        left: Math.max(rel(sp.a), 0),
        //* Un tramo de minutos colapsaría a 0%: se le deja un mínimo para que exista visualmente.
        width: Math.min(Math.max(rel(sp.b) - rel(sp.a), 1.5), 100),
        open: sp.open,
      }))
  }

  private rangeLabel(start: number, end: number): string {
    const a = new Date(start)
    const b = new Date(end)
    const fmt = (d: Date) => `${d.getDate()} ${MONTHS_ES[d.getMonth()].toLowerCase()}`
    return a.getFullYear() === b.getFullYear() ? `${fmt(a)} → ${fmt(b)} ${b.getFullYear()}` : `${fmt(a)} ${a.getFullYear()} → ${fmt(b)} ${b.getFullYear()}`
  }

  private pct(t: number): number {
    return ((t - this.t0) / this.span) * 100
  }

  // ─────────── Desglose ───────────

  /**
   * Despliega la épica y trae sus tareas la PRIMERA vez. Cargarlas todas de entrada serían 65
   * consultas para ver una.
   */
  toggle(row: GanttRow): void {
    row.expanded = !row.expanded
    if (!row.expanded || row.tasks || row.loading) return
    row.loading = true
    this.worklog.getEpicTasks(row.epic.epic.slug).subscribe((tasks) => {
      const ahora = Date.now()
      row.tasks = tasks.map((t) => this.toTaskBar(t, ahora))
      row.loading = false
    })
  }

  private toTaskBar(t: EpicTask, now: number): TaskBar {
    const area = (t.areas ?? [])[0]
    const color = (AREA_META as any)[area]?.color ?? '#94a3b8'
    const start = t.startedAt ? new Date(t.startedAt).getTime() : null
    const entregada = t.deliveredAt ? new Date(t.deliveredAt).getTime() : null
    //* Igual que en el carril suelto: el fin es la liberación, y lo entregado que aún no
    //* sale llega hasta hoy porque sigue esperando ventana.
    const liberada = t.releasedAt ? new Date(t.releasedAt).getTime() : null
    const end = liberada ?? (entregada != null && start != null ? (t.status === 'To Deploy' ? now : entregada) : entregada)

    //* Igual que en el carril suelto: sin recorrido observado la resta de las dos fechas da casi
    //* cero, y una barra de ancho mínimo se lee como «tardó nada». Se pinta como hito puntual.
    const point = !start || !end || t.observed === false
    const anchor = start ?? end
    const puntos = t.points != null ? ` · ${t.points} pts` : ''
    const dur = point ? null : durLabel((end as number) - (start as number))
    const tooltip = `${t.code} · ${t.title}${puntos}${dur ? ` · ${dur}` : ''}${t.observed === false ? ' · sin recorrido observado' : ''}`

    if (point || anchor == null) {
      return { task: t, left: anchor == null ? 0 : this.pct(anchor), width: 0, devWidth: 100, point: true, color, tooltip }
    }
    const devWidth = entregada != null
      ? Math.min(Math.max(((entregada - (start as number)) / Math.max((end as number) - (start as number), 1)) * 100, 0), 100)
      : 100
    return {
      task: t,
      left: this.pct(start as number),
      width: Math.max(this.pct(end as number) - this.pct(start as number), 0.4),
      devWidth,
      point: false,
      color,
      tooltip,
    }
  }

  onPick(row: GanttRow): void {
    this.select.emit(row.epic)
  }

  onPickLoose(row: LooseRow): void {
    this.openItem.emit(row.item._id)
  }

  onPickTask(row: TaskBar): void {
    this.openItem.emit(row.task._id)
  }

  trackEpic(_: number, r: GanttRow): string {
    return r.epic.epic.slug
  }

  trackLoose(_: number, r: LooseRow): string {
    return r.item._id
  }
}
